<template>
  <!-- «Обращения филиала» (handoff p6) -->
  <div class="support-appeals">
    <div class="support-appeals__head">
      <h1 class="support-appeals__title">
        Обращения филиала
      </h1>
      <v-btn
        depressed
        height="44"
        class="support-appeals__write text-normalize"
        @click="openForm"
      >
        <v-icon left size="18">
          mdi-pencil
        </v-icon>
        Написать в поддержку
      </v-btn>
    </div>

    <div class="support-appeals__tabs" role="tablist">
      <button
        v-for="item in tabs"
        :key="item.filter"
        type="button"
        role="tab"
        class="support-appeals__tab"
        :class="{ 'support-appeals__tab--active': item.filter === filter }"
        :aria-selected="item.filter === filter"
        @click="selectTab(item.filter)"
      >
        {{ item.label }}
        <span
          v-if="counters"
          class="support-appeals__count"
          :class="{ 'support-appeals__count--accent': item.filter === 'open' }"
        >{{ counters[item.filter] }}</span>
      </button>
    </div>

    <div v-if="loading && !items.length" class="support-appeals__list">
      <v-skeleton-loader
        v-for="n in perPage"
        :key="n"
        type="list-item-avatar-three-line"
        class="support-appeals__skeleton"
      />
    </div>

    <div v-else-if="error" class="support-appeals__state">
      <div class="support-appeals__state-title">
        Не удалось загрузить обращения
      </div>
      <v-btn depressed class="support-appeals__retry text-normalize" @click="load">
        Повторить
      </v-btn>
    </div>

    <div v-else-if="!items.length" class="support-appeals__state">
      <div class="support-appeals__state-title">
        Обращений пока нет
      </div>
      <div class="support-appeals__state-text">
        Здесь появятся обращения филиала в поддержку FAKT
      </div>
    </div>

    <div v-else class="support-appeals__list">
      <appeal-card
        v-for="appeal in items"
        :key="appeal.id"
        :appeal="appeal"
        @open="openAppeal"
      />
    </div>

    <support-pagination
      v-if="items.length"
      class="support-appeals__pagination"
      :total="total"
      :page="page"
      :per-page="perPage"
      @page="selectPage"
    />
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import supportApi from '@/api/support'
  import RouteName from '@/models/enums/RouteName'
  import AppealCard from '@/components/support/AppealCard.vue'
  import SupportPagination from '@/components/support/SupportPagination.vue'
  import { branchChannel, isConnected, subscribe } from '@/services/SupportRealtime'

  const PER_PAGE = 5
  const FILTERS = ['open', 'solved', 'all']
  // Событие канала филиала: перечитываем страницу, но не на каждое из пачки событий.
  const RELOAD_DELAY_MS = 500
  // Сокет не поднялся - опрос, как в 09-api («Если сокет отвалился…»).
  const POLL_MS = 15000

  export default {
    name: 'SupportAppeals',
    components: { AppealCard, SupportPagination },
    data () {
      return {
        tabs: [
          { filter: 'open', label: 'Открытые' },
          { filter: 'solved', label: 'Решённые' },
          { filter: 'all', label: 'Все' },
        ],
        perPage: PER_PAGE,
        items: [],
        counters: null,
        total: 0,
        loading: false,
        error: false,
        stopRealtime: null,
        reloadTimer: null,
        pollTimer: null,
      }
    },
    computed: {
      ...mapGetters({ branchId: 'branches/getGlobalFilialId' }),
      ...mapState({ formSentTick: state => state.support.formSentTick }),
      filter () {
        return FILTERS.includes(this.$route.query.tab) ? this.$route.query.tab : 'open'
      },
      page () {
        return Math.max(1, Number(this.$route.query.page) || 1)
      },
    },
    watch: {
      '$route.query': 'load',
      formSentTick: 'load',
    },
    created () {
      this.load()
      if (this.branchId) {
        this.stopRealtime = subscribe(branchChannel(this.branchId), {
          'appeal.updated': this.scheduleReload,
          'message.created': this.scheduleReload,
          'appeal.taken': this.scheduleReload,
        })
      }
      this.pollTimer = setInterval(this.poll, POLL_MS)
    },
    beforeDestroy () {
      if (this.stopRealtime) this.stopRealtime()
      clearTimeout(this.reloadTimer)
      clearInterval(this.pollTimer)
    },
    methods: {
      async load () {
        this.loading = true
        this.error = false
        try {
          const data = await supportApi.appeals(this.filter, this.page, this.perPage)
          this.items = data.items
          this.counters = data.counters
          this.total = data.total
        } catch (e) {
          this.error = true
        } finally {
          this.loading = false
        }
      },
      scheduleReload () {
        clearTimeout(this.reloadTimer)
        this.reloadTimer = setTimeout(this.load, RELOAD_DELAY_MS)
      },
      async poll () {
        if (!(await isConnected())) this.load()
      },
      selectTab (filter) {
        if (filter !== this.filter) this.$router.replace({ query: { tab: filter } })
      },
      selectPage (page) {
        this.$router.replace({ query: { ...this.$route.query, page } })
      },
      openAppeal (appeal) {
        this.$router.push({ name: RouteName.SupportAppeal, params: { id: appeal.id } })
      },
      openForm () {
        this.$store.dispatch('support/openForm')
      },
    },
  }
</script>

<style scoped lang="less">
@import '../../components/support/tokens.less';

.support-appeals {
  padding: 24px 0;
}

.support-appeals__head {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.support-appeals__title {
  font-size: 24px;
  font-weight: 500;
  color: @fakt-text;
}

.support-appeals__write {
  padding: 0 20px !important;
  border-radius: 10px;
  font-size: 14px;
  color: @fakt-white !important;
  background-color: @fakt-orange !important;
}

.support-appeals__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.support-appeals__tab {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  height: 40px;
  padding: 0 8px 0 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  color: @fakt-text;
  background: @fakt-surface;

  &--active {
    color: @fakt-white;
    background: @fakt-dark;
  }
}

.support-appeals__count {
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 500;
  line-height: 22px;
  color: @fakt-white;
  background: @fakt-text-3;

  &--accent {
    background: @fakt-orange;
  }
}

.support-appeals__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.support-appeals__skeleton {
  height: 125px;
  border-radius: 8px;
  background: @fakt-white;
}

.support-appeals__state {
  padding: 64px 16px;
  text-align: center;
}

.support-appeals__state-title {
  font-size: 16px;
  font-weight: 500;
  color: @fakt-text;
}

.support-appeals__state-text {
  margin-top: 6px;
  font-size: 14px;
  color: @fakt-text-2;
}

.support-appeals__retry {
  margin-top: 16px;
  background-color: @fakt-surface !important;
}

.support-appeals__pagination {
  margin-top: 24px;
}
</style>
